import { useState, useEffect } from 'react';
import logoUrl from '@/assets/logo.png';

interface Props {
  message?: string;
  // Auth-aware login flow: если юзер не авторизован, показываем «Войти
  // через Steam». Админ после login'а попадёт в нормальный UI (его
  // bypass'ит middleware); обычный юзер — обратно сюда.
  isAuthenticated?: boolean;
  onLogin?: () => void;
}

export function MaintenancePage({ message, isAuthenticated, onLogin }: Props) {
  const [retrying, setRetrying] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [dots, setDots] = useState('');

  useEffect(() => {
    const id = setInterval(() => {
      setDots((d) => (d.length >= 3 ? '' : d + '.'));
    }, 500);
    return () => clearInterval(id);
  }, []);

  const handleRetry = () => {
    setRetrying(true);
    setTimeout(() => window.location.reload(), 200);
  };

  const handleLogin = () => {
    if (!onLogin) return;
    setLoggingIn(true);
    onLogin();
  };

  return (
    <div className="min-h-screen bg-[#0d1117] text-white flex items-center justify-center px-4 relative overflow-hidden">
      {/* Decorative orange glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#ff6b00] opacity-[0.08] rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-md w-full text-center relative z-10">
        {/* Logo with rotating dashed ring */}
        <div className="mx-auto w-28 h-28 mb-10 relative">
          <div
            className="absolute inset-0 rounded-full border-2 border-dashed border-[#ff6b00]/40"
            style={{ animation: 'mt-spin 8s linear infinite' }}
          />
          <div className="absolute inset-2 bg-gradient-to-br from-[#ff6b00] to-[#ff9500] rounded-2xl transform rotate-3 shadow-lg shadow-[#ff6b00]/30" />
          <div className="absolute inset-2 bg-[#0d1117] rounded-2xl flex items-center justify-center border border-[#30363d]">
            <img src={logoUrl} alt="Xpanda" className="w-14 h-14 object-contain" />
          </div>
        </div>

        <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-white to-[#8b949e] bg-clip-text text-transparent">
          Скоро вернёмся{dots}
        </h1>

        <p className="text-[#8b949e] text-base mb-2 whitespace-pre-line leading-relaxed">
          {message?.trim() || 'Идут технические работы — скоро всё заработает.'}
        </p>

        <p className="text-[#6e7681] text-sm mb-10">
          Если задержались — обновите страницу.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          {/* Primary CTA: Steam-login если не залогинен — даёт быстрый
              путь админу проверить доступ. Иначе Refresh — primary. */}
          {!isAuthenticated && onLogin ? (
            <>
              <button
                onClick={handleLogin}
                disabled={loggingIn}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap px-7 py-3 bg-[#ff6b00] hover:bg-[#ff8400] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-all shadow-lg shadow-[#ff6b00]/20 hover:shadow-[#ff6b00]/40"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.004.105.004.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.627 0 11.999-5.373 11.999-12S18.605 0 11.979 0zM7.54 18.21l-1.473-.61c.262.543.714.999 1.314 1.25 1.297.539 2.793-.076 3.332-1.375.263-.63.264-1.319.005-1.949s-.75-1.121-1.377-1.383c-.624-.26-1.29-.249-1.878-.03l1.523.63c.956.4 1.409 1.5 1.009 2.455-.397.957-1.497 1.41-2.454 1.012H7.54zm11.415-9.303c0-1.662-1.353-3.015-3.015-3.015-1.665 0-3.015 1.353-3.015 3.015 0 1.665 1.35 3.015 3.015 3.015 1.663 0 3.015-1.35 3.015-3.015zm-5.273-.005c0-1.252 1.013-2.266 2.265-2.266 1.249 0 2.266 1.014 2.266 2.266 0 1.251-1.017 2.265-2.266 2.265-1.253 0-2.265-1.014-2.265-2.265z"/>
                </svg>
                {loggingIn ? 'Открываем Steam…' : 'Войти через Steam'}
              </button>
              <button
                onClick={handleRetry}
                disabled={retrying}
                className="inline-flex items-center justify-center whitespace-nowrap px-5 py-3 text-[#8b949e] hover:text-white active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed font-medium rounded-xl transition-all"
              >
                {retrying ? 'Проверяем…' : 'Обновить страницу'}
              </button>
            </>
          ) : (
            <button
              onClick={handleRetry}
              disabled={retrying}
              className="inline-flex items-center justify-center whitespace-nowrap px-7 py-3 bg-[#ff6b00] hover:bg-[#ff8400] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-all shadow-lg shadow-[#ff6b00]/20 hover:shadow-[#ff6b00]/40"
            >
              {retrying ? 'Проверяем…' : 'Обновить страницу'}
            </button>
          )}
        </div>

        <div className="mt-12 pt-6 border-t border-[#30363d]/50">
          <p className="text-[#6e7681] text-xs">
            Срочный вопрос?{' '}
            <a
              href="mailto:site@xpanda.team"
              className="text-[#ff6b00] hover:text-[#ff8400] hover:underline transition-colors"
            >
              site@xpanda.team
            </a>
          </p>
        </div>
      </div>

      <style>{`
        @keyframes mt-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
